import React, { useMemo } from "react";
import { Layers, Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

/**
 * Phase 207 / 209 — Floor-wise assessment editor for the citizen SAF and the collector's verification screen.
 * Every row carries EXACTLY the fields of Admin → Manual Entry:
 *   Floor · House No · Property type · Construction type
 *   Usage type · Sub-usage · Category (User Charges) · Sub-category
 *   Plot area · Open area · Constructed area · Carpet area (sqft)
 *   Electricity CA No · Construction Date · Construction Age (yrs, auto from the date)
 * Constructed auto = Plot − Open, Carpet auto = 80% of constructed (both editable).
 * `masters` = the object returned by `useMasterOptions(code)` (keys: floors, usage-types, sub-usage-types,
 * construction-types, property-types, categories, sub-categories).
 */
export const EMPTY_SAF_FLOOR = {
  floor_label: "", house_no: "", property_type_id: "", const_type_id: "", usage_type_id: "", sub_usage_id: "",
  fee_category_id: "", fee_sub_category_id: "", plot_area_sqft: "", open_area_sqft: "", constructed_area_sqft: "", carpet_area_sqft: "",
  electricity_ca_no: "", construction_date: "", construction_age: "",
};
const FLOOR_FALLBACK = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Fourth Floor", "Fifth Floor"];

/** Floor master labels in the natural order a citizen adds them: Ground, First, Second… then Basement and other below-ground floors. */
export const orderedFloorLabels = (masters = {}) => {
  const sorted = [...(masters.floors || [])].sort((a, b) => (Number(a.floor_number) || 0) - (Number(b.floor_number) || 0));
  const above = sorted.filter((f) => (Number(f.floor_number) || 0) >= 0);
  const below = sorted.filter((f) => (Number(f.floor_number) || 0) < 0);
  return [...above, ...below].map((f) => f.floor_label);
};

export const newSafFloor = (idx = 0, masters = {}) => {
  const labels = orderedFloorLabels(masters);
  return { ...EMPTY_SAF_FLOOR, floor_label: labels[idx] || FLOOR_FALLBACK[idx] || `Floor ${idx + 1}` };
};

/** Construction age in whole years from an ISO date — same rule as Manual Entry. */
export const ageFromDate = (d) => {
  if (!d) return "";
  const y = new Date(d).getFullYear();
  if (Number.isNaN(y)) return "";
  const age = new Date().getFullYear() - y;
  return age >= 0 ? age : "";
};

/** Server floor rows (names snapshotted) -> editor rows (string inputs). */
export const floorsToEditor = (floors) => (floors || []).map((f) => ({
  ...EMPTY_SAF_FLOOR, ...f,
  house_no: f.house_no || "",
  plot_area_sqft: f.plot_area_sqft != null ? String(f.plot_area_sqft) : "",
  open_area_sqft: f.open_area_sqft != null ? String(f.open_area_sqft) : "",
  constructed_area_sqft: f.constructed_area_sqft != null ? String(f.constructed_area_sqft) : (f.area_sqft != null ? String(f.area_sqft) : ""),
  carpet_area_sqft: f.carpet_area_sqft != null ? String(f.carpet_area_sqft) : "",
  electricity_ca_no: f.electricity_ca_no || "",
  construction_date: (f.construction_date || "").slice(0, 10),
  construction_age: f.construction_age != null && f.construction_age !== "" ? Number(f.construction_age) : ageFromDate(f.construction_date),
}));

const num = (v) => Number(v) || 0;
export const sumFloors = (floors, k) => Math.round((floors || []).reduce((s, f) => s + num(f[k]), 0) * 100) / 100;
export const floorTotals = (floors) => ({
  plot: sumFloors(floors, "plot_area_sqft"), open: sumFloors(floors, "open_area_sqft"),
  constructed: sumFloors(floors, "constructed_area_sqft"), carpet: sumFloors(floors, "carpet_area_sqft"),
});
export const isFloorFilled = (f) => !!(f.usage_type_id || f.const_type_id || f.property_type_id || num(f.constructed_area_sqft) > 0 || num(f.plot_area_sqft) > 0);
/** Editor rows -> API payload (numbers). */
export const floorsPayload = (floors) => (floors || []).filter(isFloorFilled).map((f) => ({
  floor_label: f.floor_label || "Floor", house_no: (f.house_no || "").trim() || null,
  property_type_id: f.property_type_id || null, const_type_id: f.const_type_id || null,
  usage_type_id: f.usage_type_id || null, sub_usage_id: f.sub_usage_id || null,
  fee_category_id: f.fee_category_id || null, fee_sub_category_id: f.fee_sub_category_id || null,
  plot_area_sqft: num(f.plot_area_sqft), open_area_sqft: num(f.open_area_sqft),
  constructed_area_sqft: num(f.constructed_area_sqft), carpet_area_sqft: num(f.carpet_area_sqft),
  electricity_ca_no: (f.electricity_ca_no || "").trim() || null,
  construction_date: f.construction_date || null,
  construction_age: f.construction_age === "" || f.construction_age == null ? null : Number(f.construction_age),
}));

const Pick = ({ value, onChange, options, labelKey, placeholder, disabled, testId }) => {
  const sel = (options || []).find((o) => o.id === value);
  return (
    <Select value={value || ""} onValueChange={(v) => v && onChange(v)} disabled={disabled}>
      <SelectTrigger className="h-10" data-testid={testId} aria-label={placeholder}>
        <SelectValue placeholder={placeholder}>{sel ? sel[labelKey] : null}</SelectValue>
      </SelectTrigger>
      <SelectContent className="max-h-64">
        {(options || []).map((o) => <SelectItem key={o.id} value={o.id}>{o[labelKey]}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-0.5 min-w-0">
    <Label className="text-[11px]">{label}</Label>
    {children}
  </div>
);

export function SafFloorEditor({ masters = {}, floors, setFloors, readOnly = false, testIdPrefix = "saf-floor", title = "Floor-wise assessment",
  description = "Add one row per floor. Constructed area auto-fills as Plot − Open; carpet area as 80% of constructed — both can be edited.",
  showCategory = true, compact = false }) {
  const floorOpts = useMemo(() => orderedFloorLabels(masters).map((l) => ({ id: l, floor_label: l })), [masters]);
  const subUsageOf = (uid) => (masters["sub-usage-types"] || []).filter((s) => s.usage_type_id === uid);
  const subCatOf = (cid) => (masters["sub-categories"] || []).filter((s) => s.category_id === cid);
  const nameOf = (list, id, key) => (masters[list] || []).find((o) => o.id === id)?.[key] || "";

  const setFloor = (i, k, v) => setFloors((fs) => fs.map((f, j) => {
    if (j !== i) return f;
    const next = { ...f, [k]: v };
    if (k === "usage_type_id") next.sub_usage_id = "";
    if (k === "fee_category_id") next.fee_sub_category_id = "";
    if (k === "plot_area_sqft" || k === "open_area_sqft") {
      const plot = num(k === "plot_area_sqft" ? v : next.plot_area_sqft);
      const open = num(k === "open_area_sqft" ? v : next.open_area_sqft);
      if (plot > 0) {
        const constructed = Math.max(0, Math.round((plot - open) * 100) / 100);
        next.constructed_area_sqft = String(constructed);
        next.carpet_area_sqft = String(Math.round(constructed * 0.8 * 100) / 100);
      }
    }
    if (k === "constructed_area_sqft") next.carpet_area_sqft = v && !isNaN(Number(v)) ? String(Math.round(Number(v) * 0.8 * 100) / 100) : f.carpet_area_sqft;
    if (k === "construction_date") {
      const age = ageFromDate(v);
      next.construction_age = v && age !== "" ? age : f.construction_age;
    }
    return next;
  }));
  const add = () => setFloors((fs) => [...fs, newSafFloor(fs.length, masters)]);
  const remove = (i) => setFloors((fs) => fs.filter((_, j) => j !== i));
  const filled = floors.filter(isFloorFilled);
  const totals = floorTotals(filled);

  const Wrapper = compact ? "div" : Card;
  return (
    <Wrapper className={compact ? "space-y-3" : "shadow-sm"} data-testid={`${testIdPrefix}-section`}>
      {!compact && (
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Layers className="h-4 w-4" /> {title}</CardTitle>
          <p className="text-xs text-muted-foreground">{description}</p>
        </CardHeader>
      )}
      <CardContent className={compact ? "p-0 space-y-3" : "space-y-3"}>
        {!readOnly && floors.map((f, i) => (
          <div key={i} className="border rounded-lg p-3 space-y-2 bg-card" data-testid={`${testIdPrefix}-row-${i}`}>
            {/* 1 — Floor · House No · Property type · Construction type */}
            <div className="flex items-start justify-between gap-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 flex-1">
                <Field label="Floor *">
                  {floorOpts.length > 0 ? (
                    <Pick value={f.floor_label} onChange={(v) => setFloor(i, "floor_label", v)} options={floorOpts} labelKey="floor_label"
                      placeholder="Floor" disabled={readOnly} testId={`${testIdPrefix}-floor-${i}`} />
                  ) : (
                    <Input className="h-10" value={f.floor_label} placeholder="e.g. Ground Floor" disabled={readOnly}
                      onChange={(e) => setFloor(i, "floor_label", e.target.value)} data-testid={`${testIdPrefix}-floor-${i}`} />
                  )}
                </Field>
                <Field label="House No">
                  <Input className="h-10" value={f.house_no} placeholder="House No." disabled={readOnly}
                    onChange={(e) => setFloor(i, "house_no", e.target.value)} data-testid={`${testIdPrefix}-houseno-${i}`} />
                </Field>
                <Field label="Property type">
                  <Pick value={f.property_type_id} onChange={(v) => setFloor(i, "property_type_id", v)} options={masters["property-types"]} labelKey="type_name"
                    placeholder="Property type" disabled={readOnly} testId={`${testIdPrefix}-ptype-${i}`} />
                </Field>
                <Field label="Construction type *">
                  <Pick value={f.const_type_id} onChange={(v) => setFloor(i, "const_type_id", v)} options={masters["construction-types"]} labelKey="const_type_name"
                    placeholder="Construction type" disabled={readOnly} testId={`${testIdPrefix}-ctype-${i}`} />
                </Field>
              </div>
              {!readOnly && floors.length > 1 && (
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 mt-5 shrink-0" onClick={() => remove(i)}
                  aria-label="Remove floor" data-testid={`${testIdPrefix}-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              )}
            </div>
            {/* 2 — Usage · Sub-usage · Category · Sub-category */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${showCategory ? "lg:grid-cols-4" : ""} gap-2`}>
              <Field label="Usage type *">
                <Pick value={f.usage_type_id} onChange={(v) => setFloor(i, "usage_type_id", v)} options={masters["usage-types"]} labelKey="usage_type_name"
                  placeholder="Usage type (Property Tax)" disabled={readOnly} testId={`${testIdPrefix}-usage-${i}`} />
              </Field>
              <Field label="Sub-usage">
                <Pick value={f.sub_usage_id} onChange={(v) => setFloor(i, "sub_usage_id", v)} options={subUsageOf(f.usage_type_id)} labelKey="sub_usage_type_name"
                  placeholder={!f.usage_type_id ? "Pick usage first" : (subUsageOf(f.usage_type_id).length === 0 ? "No sub-usages" : "Sub-usage")}
                  disabled={readOnly || !f.usage_type_id || subUsageOf(f.usage_type_id).length === 0} testId={`${testIdPrefix}-subusage-${i}`} />
              </Field>
              {showCategory && (
                <>
                  <Field label="Category (User Charges)">
                    <Pick value={f.fee_category_id} onChange={(v) => setFloor(i, "fee_category_id", v)} options={masters.categories} labelKey="category_name"
                      placeholder="Category" disabled={readOnly} testId={`${testIdPrefix}-category-${i}`} />
                  </Field>
                  <Field label="Sub-category">
                    <Pick value={f.fee_sub_category_id} onChange={(v) => setFloor(i, "fee_sub_category_id", v)} options={subCatOf(f.fee_category_id)} labelKey="sub_category_name"
                      placeholder={!f.fee_category_id ? "Pick category first" : (subCatOf(f.fee_category_id).length === 0 ? "No sub-categories" : "Sub-category")}
                      disabled={readOnly || !f.fee_category_id || subCatOf(f.fee_category_id).length === 0} testId={`${testIdPrefix}-subcategory-${i}`} />
                  </Field>
                </>
              )}
            </div>
            {/* 3 — Areas */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[["plot_area_sqft", "Plot area (sqft)"], ["open_area_sqft", "Open area (sqft)"], ["constructed_area_sqft", "Constructed area (sqft) *"], ["carpet_area_sqft", "Carpet area (sqft)"]].map(([k, lbl]) => (
                <Field key={k} label={lbl}>
                  <Input className="h-10" type="number" step="any" min="0" inputMode="decimal" value={f[k]} disabled={readOnly}
                    onChange={(e) => setFloor(i, k, e.target.value)} data-testid={`${testIdPrefix}-${k.replace("_area_sqft", "")}-${i}`} />
                </Field>
              ))}
            </div>
            {/* 4 — Electricity · Construction date · Construction age (auto) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Field label="Electricity CA No">
                <Input className="h-10" value={f.electricity_ca_no} placeholder="CA number" disabled={readOnly}
                  onChange={(e) => setFloor(i, "electricity_ca_no", e.target.value)} data-testid={`${testIdPrefix}-electricity-${i}`} />
              </Field>
              <Field label="Construction Date">
                <Input className="h-10" type="date" value={f.construction_date} disabled={readOnly} max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setFloor(i, "construction_date", e.target.value)} data-testid={`${testIdPrefix}-constdate-${i}`} />
              </Field>
              <Field label="Construction Age (yrs, auto)">
                <Input className="h-10" type="number" min="0" value={f.construction_age} placeholder="Years" disabled={readOnly}
                  onChange={(e) => setFloor(i, "construction_age", e.target.value === "" ? "" : Number(e.target.value))} data-testid={`${testIdPrefix}-constage-${i}`} />
              </Field>
            </div>
          </div>
        ))}
        {!readOnly && (
          <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`${testIdPrefix}-add`}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add floor
          </Button>
        )}
        {readOnly && filled.length === 0 && <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-none`}>No floor details recorded.</p>}
        {filled.length > 0 && (
          <div className="border rounded-lg overflow-x-auto" data-testid={`${testIdPrefix}-summary`}>
            {!readOnly && <p className="text-xs font-semibold px-3 pt-2.5 pb-1">Summary sheet</p>}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Floor</TableHead>
                  <TableHead className="text-xs">House No</TableHead>
                  <TableHead className="text-xs">Property type</TableHead>
                  <TableHead className="text-xs">Construction</TableHead>
                  <TableHead className="text-xs">Usage / Sub-usage</TableHead>
                  {showCategory && <TableHead className="text-xs">Category</TableHead>}
                  <TableHead className="text-xs text-right">Plot</TableHead>
                  <TableHead className="text-xs text-right">Open</TableHead>
                  <TableHead className="text-xs text-right">Constructed</TableHead>
                  <TableHead className="text-xs text-right">Carpet</TableHead>
                  <TableHead className="text-xs">Elec. CA No</TableHead>
                  <TableHead className="text-xs text-right">Age</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filled.map((f, i) => (
                  <TableRow key={i} data-testid={`${testIdPrefix}-summary-row-${i}`}>
                    <TableCell className="text-xs font-medium whitespace-nowrap">{f.floor_label || `Floor ${i + 1}`}</TableCell>
                    <TableCell className="text-xs">{f.house_no || "—"}</TableCell>
                    <TableCell className="text-xs">{nameOf("property-types", f.property_type_id, "type_name") || f.property_type_name || "—"}</TableCell>
                    <TableCell className="text-xs">{nameOf("construction-types", f.const_type_id, "const_type_name") || f.const_type_name || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {nameOf("usage-types", f.usage_type_id, "usage_type_name") || f.usage_type_name || "—"}
                      {(nameOf("sub-usage-types", f.sub_usage_id, "sub_usage_type_name") || f.sub_usage_name) ? <span className="text-muted-foreground"> / {nameOf("sub-usage-types", f.sub_usage_id, "sub_usage_type_name") || f.sub_usage_name}</span> : ""}
                    </TableCell>
                    {showCategory && (
                      <TableCell className="text-xs">
                        {nameOf("categories", f.fee_category_id, "category_name") || f.fee_category_name || "—"}
                        {(nameOf("sub-categories", f.fee_sub_category_id, "sub_category_name") || f.fee_sub_category_name) ? <span className="text-muted-foreground"> / {nameOf("sub-categories", f.fee_sub_category_id, "sub_category_name") || f.fee_sub_category_name}</span> : ""}
                      </TableCell>
                    )}
                    <TableCell className="text-xs text-right tabular-nums">{num(f.plot_area_sqft)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{num(f.open_area_sqft)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{num(f.constructed_area_sqft)}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{num(f.carpet_area_sqft)}</TableCell>
                    <TableCell className="text-xs">{f.electricity_ca_no || "—"}</TableCell>
                    <TableCell className="text-xs text-right tabular-nums">{f.construction_age !== "" && f.construction_age != null ? f.construction_age : "—"}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/60 font-semibold" data-testid={`${testIdPrefix}-summary-total`}>
                  <TableCell className="text-xs" colSpan={showCategory ? 6 : 5}>Total ({filled.length} floor{filled.length > 1 ? "s" : ""})</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{totals.plot}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{totals.open}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums" data-testid={`${testIdPrefix}-total-constructed`}>{totals.constructed}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums" data-testid={`${testIdPrefix}-total-carpet`}>{totals.carpet}</TableCell>
                  <TableCell className="text-xs" colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Wrapper>
  );
}
